import { useState, useEffect } from 'react';
import { Navigation } from '../../components/Navigation';
import { FloatingBackButton } from '../../components/FloatingBackButton';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';
import {
  Briefcase,
  DollarSign,
  MapPin,
  Clock,
  Calendar,
  Filter,
  Search,
  TrendingUp,
  Star,
  Send,
  ExternalLink,
  Wifi
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface Gig {
  id: string;
  title: string;
  description: string;
  category: string;
  skills_required: string[];
  gig_type: string;
  duration_estimate: string;
  budget_min: number;
  budget_max: number;
  budget_type: string;
  remote_type: string;
  time_zone_preference: string[];
  collaboration_tools: string[];
  applications_count: number;
  deadline: string | null;
  created_at: string;
  employer: {
    full_name: string;
    company_name?: string;
  };
}

const CATEGORIES = [
  'All',
  'Development',
  'Design',
  'Writing',
  'Marketing',
  'Consulting',
  'Data Analysis',
  'Video Editing',
  'Virtual Assistant',
];

const REMOTE_TYPES = [
  { value: 'all', label: 'All' },
  { value: 'fully_remote', label: 'Fully Remote' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'remote_friendly', label: 'Remote Friendly' },
];

export function GigBoard() {
  const { user } = useAuthStore();
  const [gigs, setGigs] = useState<Gig[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedRemoteType, setSelectedRemoteType] = useState('all');

  useEffect(() => {
    loadGigs();
  }, [selectedCategory, selectedRemoteType]);

  const loadGigs = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('gigs')
        .select(`
          *,
          employer:employer_id (full_name, company_name)
        `)
        .eq('status', 'open')
        .not('published_at', 'is', null)
        .order('created_at', { ascending: false });

      if (selectedCategory !== 'All') {
        query = query.eq('category', selectedCategory.toLowerCase());
      }

      if (selectedRemoteType !== 'all') {
        query = query.eq('remote_type', selectedRemoteType);
      }

      const { data, error } = await query;

      if (error) throw error;
      setGigs(data || []);
    } catch (error) {
      console.error('Error loading gigs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredGigs = gigs.filter(gig =>
    searchQuery === '' ||
    gig.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    gig.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    gig.skills_required.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <FloatingBackButton />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-600 to-emerald-600 flex items-center justify-center">
              <Briefcase className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gig Board</h1>
              <p className="text-gray-600">Find freelance & contract opportunities</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <Briefcase className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-gray-600">Open Gigs</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{gigs.length}</p>
            </div>
            <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <Wifi className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Remote Only</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {gigs.filter(g => g.remote_type === 'fully_remote').length}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-gray-600">New This Week</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {gigs.filter(g => {
                  const created = new Date(g.created_at);
                  const weekAgo = new Date();
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  return created > weekAgo;
                }).length}
              </p>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search gigs by title, description, or skills..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none"
              />
            </div>

            {/* Category Filters */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {CATEGORIES.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                    selectedCategory === category
                      ? 'bg-green-600 text-white'
                      : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-green-300'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Remote Type Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Remote Type:</span>
              {REMOTE_TYPES.map(type => (
                <button
                  key={type.value}
                  onClick={() => setSelectedRemoteType(type.value)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    selectedRemoteType === type.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-300'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Gig List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          </div>
        ) : filteredGigs.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border-2 border-gray-200">
            <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No gigs found</h3>
            <p className="text-gray-600">Try adjusting your filters or search query</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredGigs.map(gig => (
              <GigCard key={gig.id} gig={gig} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

interface GigCardProps {
  gig: Gig;
}

function GigCard({ gig }: GigCardProps) {
  const daysAgo = Math.floor((Date.now() - new Date(gig.created_at).getTime()) / (1000 * 60 * 60 * 24));
  const deadline = gig.deadline ? new Date(gig.deadline) : null;

  return (
    <div className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-green-300 hover:shadow-lg transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-xl font-bold text-gray-900">{gig.title}</h3>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
              gig.remote_type === 'fully_remote'
                ? 'bg-blue-100 text-blue-700'
                : gig.remote_type === 'hybrid'
                ? 'bg-purple-100 text-purple-700'
                : 'bg-gray-100 text-gray-700'
            }`}>
              {gig.remote_type.replace('_', ' ')}
            </span>
          </div>

          <p className="text-gray-600 mb-3 line-clamp-2">{gig.description}</p>

          {/* Skills */}
          {gig.skills_required && gig.skills_required.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {gig.skills_required.slice(0, 5).map((skill, idx) => (
                <span
                  key={idx}
                  className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded"
                >
                  {skill}
                </span>
              ))}
              {gig.skills_required.length > 5 && (
                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                  +{gig.skills_required.length - 5} more
                </span>
              )}
            </div>
          )}

          {/* Details */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
            {gig.budget_min && gig.budget_max && (
              <div className="flex items-center gap-1">
                <DollarSign className="w-4 h-4" />
                <span>${gig.budget_min.toLocaleString()} - ${gig.budget_max.toLocaleString()}</span>
                <span className="text-xs text-gray-500">({gig.budget_type})</span>
              </div>
            )}
            {gig.duration_estimate && (
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{gig.duration_estimate}</span>
              </div>
            )}
            {deadline && (
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>Due {deadline.toLocaleDateString()}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <Send className="w-4 h-4" />
              <span>{gig.applications_count} applications</span>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <button className="ml-4 px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center gap-2">
          Apply Now
          <ExternalLink className="w-4 h-4" />
        </button>
      </div>

      {/* Footer */}
      <div className="pt-4 border-t border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-white font-bold text-sm">
            {gig.employer.full_name?.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">
              {gig.employer.company_name || gig.employer.full_name}
            </p>
            <p className="text-xs text-gray-500">Posted {daysAgo === 0 ? 'today' : `${daysAgo} days ago`}</p>
          </div>
        </div>

        {gig.collaboration_tools && gig.collaboration_tools.length > 0 && (
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-500">Tools:</span>
            {gig.collaboration_tools.slice(0, 3).map((tool, idx) => (
              <span key={idx} className="px-2 py-1 bg-gray-50 text-gray-600 text-xs rounded">
                {tool}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
